import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, FolderOpen } from 'lucide-react';
import { PortfolioProjectCard } from './portfolio-project-card';
import { PortfolioProjectForm } from './portfolio-project-form';
import { usePortfolioProjects } from '@/lib/hooks/use-portfolio-projects';
import { IncompleteSectionWrapper } from '@/components/profile/incomplete-section-wrapper';
import { SectionHeader } from '@/components/profile/section-header';
import { EmptySectionPlaceholder } from '@/components/profile/empty-section-placeholder';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Tables, TablesInsert } from '@/lib/supabase/supabase-types';

type PortfolioProject = Tables<'portfolio_projects'>;

interface PortfolioSectionProps {
  freelancerProfileId?: string;
  isOwner?: boolean;
  className?: string;
}

export function PortfolioSection({ freelancerProfileId, isOwner = false, className }: PortfolioSectionProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<PortfolioProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<PortfolioProject | null>(null);

  const {
    projects,
    isLoading,
    createProject,
    updateProject,
    deleteProject,
    isCreating,
    isUpdating,
    isDeleting,
  } = usePortfolioProjects(freelancerProfileId);

  const handleCreateProject = async (data: Omit<TablesInsert<'portfolio_projects'>, 'freelancer_profile_id'>) => {
    try {
      await createProject(data);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleUpdateProject = async (data: { id: string } & Partial<PortfolioProject>) => {
    try {
      await updateProject(data);
      setEditingProject(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;

    try {
      await deleteProject(deletingProject.id);
      setDeletingProject(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleEditProject = (project: PortfolioProject) => {
    setEditingProject(project);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingProject(null);
  };

  const hasProjects = projects.length > 0;

  return (
    <IncompleteSectionWrapper isComplete={hasProjects || !isOwner} className={className}>
      <SectionHeader
        icon={FolderOpen}
        title="Portfolio & Projects"
        isComplete={hasProjects || !isOwner}
      />

      {isLoading && (
        <div className="text-center py-8">
          <p className="text-slate-400">Loading projects...</p>
        </div>
      )}

      {!isLoading && !hasProjects && isOwner && (
        <EmptySectionPlaceholder
          icon={FolderOpen}
          title="Showcase Your Best Work"
          description="Add portfolio projects to demonstrate your AI/ML expertise and attract more clients."
          buttonLabel="Add Your First Project"
          onButtonClick={() => setIsFormOpen(true)}
        />
      )}

      {!isLoading && !hasProjects && !isOwner && (
        <div className="text-center py-8">
          <p className="text-slate-400">No projects to display.</p>
        </div>
      )}

      {hasProjects && (
        <>
          {isOwner && (
            <div className="mb-6">
              <Button
                onClick={() => setIsFormOpen(true)}
                variant="outline"
                className="rounded-[48px] border-2 border-slate-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Project
              </Button>
            </div>
          )}
          <div className="grid gap-6 md:grid-cols-2">
            {projects.map((project) => (
              <PortfolioProjectCard
                key={project.id}
                project={project}
                isOwner={isOwner}
                onEdit={handleEditProject}
                onDelete={(project) => setDeletingProject(project)}
              />
            ))}
          </div>
        </>
      )}

      {/* Project Form Modal */}
      <PortfolioProjectForm
        project={editingProject || undefined}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleCreateProject}
        onUpdate={handleUpdateProject}
        isLoading={isCreating || isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingProject} onOpenChange={() => setDeletingProject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingProject?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProject}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </IncompleteSectionWrapper>
  );
}
